import React, { Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Loading } from '@carbon/react';
import RequireCapability from 'src/components/Base/RequireCapability';
import AuthenticationLayout from 'src/components/Layout/AuthenticationLayout';
import MainLayout from 'src/components/Layout/MainLayout';
import { ContextLoader } from 'src/context/ContextLoader';
import { LegacyOrgShim } from 'src/context/LegacyOrgShim';
import AcceptInvitation from 'src/pages/AcceptInvitation';
import Accounting from 'src/pages/Accounting';
import Admin from 'src/pages/Admin';
import AdminOrganizations from 'src/pages/Admin/Organizations';
import AdminUsers from 'src/pages/Admin/Users';
import AllEmployees from 'src/pages/AllEmployees';
import AllIssues from 'src/pages/AllIssues';
import AllOrders from 'src/pages/AllOrders';
import Dashboard from 'src/pages/Dashboard';
import DashboardAccounting from 'src/pages/Dashboard/Accounting';
import DashboardInventory from 'src/pages/Dashboard/Inventory';
import DashboardIssues from 'src/pages/Dashboard/Issues';
import DashboardSelling from 'src/pages/Dashboard/Selling';
import DashboardStaff from 'src/pages/Dashboard/Staff';
import Delivery from 'src/pages/Delivery';
import EmployeeDashboard from 'src/pages/EmployeeDashboard';
import Employees from 'src/pages/Employees';
import EmployeeTimesheetDetail from 'src/pages/EmployeeTimesheetDetail';
import EmployeeTimesheets from 'src/pages/EmployeeTimesheets';
import ForgotPassword from 'src/pages/ForgotPassword';
import Inventory from 'src/pages/Inventory';
import InventoryCategories from 'src/pages/InventoryCategories';
import InventoryItems from 'src/pages/InventoryItems';
import InventoryMovements from 'src/pages/InventoryMovements';
import Issues from 'src/pages/Issues';
import Kitchen from 'src/pages/Kitchen';
import MyEmployment from 'src/pages/MyEmployment';
import Onboarding from 'src/pages/Onboarding';
import Orders from 'src/pages/Orders';
import POS from 'src/pages/POS';
import Profile from 'src/pages/Profile';
import ResetPassword from 'src/pages/ResetPassword';
import SignIn from 'src/pages/SignIn';
import SignUp from 'src/pages/SignUp';
import TimesheetKiosk from 'src/pages/TimesheetKiosk';
import Transactions from 'src/pages/Transactions';
import VerifyEmail from 'src/pages/VerifyEmail';
import { settingsLeafRoutes } from 'src/routes/settingsRoutes';
import { storefrontLeafRoutes } from 'src/routes/storefrontRoutes';

const TimesheetCalendarPage = React.lazy(() => import('src/pages/TimesheetCalendarPage'));

const tenantLeafRoutes = (
  <Route element={<MainLayout />}>
    <Route index element={<Navigate to="dashboard/selling" replace />} />
    <Route path="dashboard" element={<Dashboard />}>
      <Route path="selling" element={<DashboardSelling />} />
      <Route path="accounting" element={<DashboardAccounting />} />
      <Route path="inventory" element={<DashboardInventory />} />
      <Route path="staff" element={<DashboardStaff />} />
      <Route path="issues" element={<DashboardIssues />} />
    </Route>
    <Route path="orders" element={<Orders />}>
      <Route path="today" element={<AllOrders today={true} />} />
      <Route path="all" element={<AllOrders today={false} />} />
    </Route>
    <Route path="employees" element={<Employees />}>
      <Route path="all" element={<AllEmployees />} />
      <Route path="issues" element={<AllIssues employee={true} dish={false} operation={false} />} />
      <Route path="timesheets" element={<EmployeeTimesheets />} />
      <Route path="timesheets/:timesheetId" element={<EmployeeTimesheetDetail />} />
    </Route>
    <Route path="employees/dashboard" element={<EmployeeDashboard />} />
    {/* Staff-facing self view. Deliberately outside `employees`, whose children render inside
        the owner-facing Employees tab shell that a member has no reason to see. */}
    <Route path="me" element={<MyEmployment />} />
    <Route
      path="me/calendar"
      element={
        <Suspense fallback={<Loading withOverlay={false} />}>
          <TimesheetCalendarPage mode="self" />
        </Suspense>
      }
    />
    <Route path="accounting" element={<Accounting />}>
      <Route path="transactions" element={<Transactions expenses={false} incomes={false} investments={false} />} />
      <Route path="expenses" element={<Transactions expenses={true} incomes={false} investments={false} />} />
      <Route path="incomes" element={<Transactions expenses={false} incomes={true} investments={false} />} />
      <Route path="investments" element={<Transactions expenses={false} incomes={false} investments={true} />} />
    </Route>
    <Route path="inventory" element={<Inventory />}>
      <Route path="items" element={<InventoryItems />} />
      <Route path="movements" element={<InventoryMovements />} />
      <Route path="categories" element={<InventoryCategories />} />
    </Route>
    <Route path="issues" element={<Issues />}>
      <Route path="all" element={<AllIssues employee={false} dish={false} operation={false} />} />
      <Route path="employee" element={<AllIssues employee={true} dish={false} operation={false} />} />
      <Route path="dish" element={<AllIssues employee={false} dish={true} operation={false} />} />
      <Route path="operation" element={<AllIssues employee={false} dish={false} operation={true} />} />
    </Route>
    <Route path="delivery" element={<Delivery />} />
    <Route path="kitchen" element={<Kitchen />} />
    <Route path="pos" element={<POS />} />
    <Route path="timesheets/kiosk" element={<TimesheetKiosk />} />
    <Route
      path="timesheets/calendar"
      element={
        <RequireCapability capability="canManageRestaurant" fallbackPath="/me">
          <Suspense fallback={<Loading withOverlay={false} />}>
            <TimesheetCalendarPage mode="branch" />
          </Suspense>
        </RequireCapability>
      }
    />
  </Route>
);

const orgLeafRoutes = (
  <Route element={<MainLayout />}>
    <Route path="profile" element={<Profile />} />
  </Route>
);

const App = () => (
  <Routes>
    <Route element={<AuthenticationLayout />}>
      <Route path="/sign-in" element={<SignIn />} />
      <Route path="/sign-up" element={<SignUp />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/accept-invitation/:invitationId" element={<AcceptInvitation />} />
      {/* Legacy deep links from bookmarks and the old PWA shell. */}
      <Route path="/auth/login" element={<Navigate to="/sign-in" replace />} />
    </Route>

    <Route path="/onboarding" element={<Onboarding />} />

    {/* Short in-app routes — context from cookie */}
    <Route element={<ContextLoader />}>
      {orgLeafRoutes}
      {settingsLeafRoutes}
      {storefrontLeafRoutes}
      {tenantLeafRoutes}
    </Route>

    {/* Deep-link shim: validate, set cookie, redirect to short path */}
    <Route path="/o/:orgSlug" element={<ContextLoader />} />
    <Route path="/o/:orgSlug/r/:restaurantId" element={<ContextLoader />} />
    <Route path="/o/:orgSlug/r/:restaurantId/b/:branchId/*" element={<ContextLoader />} />
    {/* Legacy org-level deep links (/o/:slug/org-settings, /o/:slug/profile, ...) */}
    <Route path="/o/:orgSlug/*" element={<LegacyOrgShim />} />

    <Route path="/admin" element={<Admin />}>
      <Route index element={<AdminOrganizations />} />
      <Route path="organizations" element={<AdminOrganizations />} />
      <Route path="users" element={<AdminUsers />} />
    </Route>

    <Route path="*" element={<ContextLoader />} />
  </Routes>
);

export default App;
